import React, { useState } from 'react'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Text from 'components_new/atoms/Text'

import SidebarDialog from 'components_new/molecules/SidebarDialog'

// Past this many the already excluded fold into "+n till".
const MAX_ALREADY_EXCLUDED = 8

interface RerunAnalysisDialogProps {
  /** Excluded from the analysis before this one. */
  alreadyExcluded: string[]

  /** The property to exclude; the dialog is open while it is set. */
  name: string | null
  onClose: () => void
  onConfirm: (callback: () => void) => void
}

/** Confirms excluding a property and running the analysis again. */
const RerunAnalysisDialog = (props: RerunAnalysisDialogProps) => {
  const { alreadyExcluded, name, onClose, onConfirm } = props

  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  const others = alreadyExcluded.filter((item) => item !== name)
  const shown = others.slice(0, MAX_ALREADY_EXCLUDED)
  const hidden = others.length - shown.length

  const handleConfirm = () => {
    setLoading(true)
    onConfirm(() => setLoading(false))
  }

  return (
    <SidebarDialog
      actions={
        <>
          <Button onClick={onClose} variant="text">
            {t('global', 'cancel')}
          </Button>
          <Button color="primary" loading={loading} onClick={handleConfirm}>
            {t('analyses', 'rerun_submit')}
          </Button>
        </>
      }
      maxWidth="xs"
      onClose={onClose}
      open={Boolean(name)}
      title={t('analyses', 'rerun_title')}
    >
      <Text sx={{ mb: 2 }}>
        {t('analyses', 'rerun_description', false, { name: name ?? '' })}
      </Text>

      <Text sx={{ mb: 0.5 }} variant="subtitle2">
        {t('analyses', 'rerun_excluded_now')}
      </Text>
      <Box sx={{ display: 'flex', mb: 2 }}>
        <Chip label={name} size="small" />
      </Box>

      {others.length > 0 ? (
        <>
          <Text sx={{ mb: 0.5 }} variant="subtitle2">
            {t('analyses', 'rerun_already_excluded')}
          </Text>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {shown.map((item) => (
              <Chip key={item} label={item} size="small" />
            ))}
            {hidden > 0 ? (
              <Chip
                label={t('analysis_types', 'excluded_more', false, {
                  count: String(hidden)
                })}
                size="small"
              />
            ) : null}
          </Box>
        </>
      ) : null}

      <Text color="text.secondary" variant="body2">
        {t('analyses', 'rerun_note')}
      </Text>
    </SidebarDialog>
  )
}

export default RerunAnalysisDialog
